import React, { useEffect, useRef, useState } from 'react';

/**
 * Lightbox — fullscreen image viewer.
 * Click anywhere to close. Click the image again to toggle zoom.
 * No transitions/animations (per site-wide effects removal).
 */
const Lightbox = ({ src, alt = '', open, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState('50% 50%');
  const imgRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setZoomed(false);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleImgClick = (e) => {
    e.stopPropagation();
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
    setZoomed((z) => !z);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
      data-testid="lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 text-white text-lg flex items-center justify-center hover:bg-white/25"
      >
        ✕
      </button>
      <div
        className="relative max-w-[95vw] max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onClick={handleImgClick}
          data-testid="lightbox-image"
          style={{
            transform: zoomed ? 'scale(2.4)' : 'scale(1)',
            transformOrigin: origin,
            cursor: zoomed ? 'zoom-out' : 'zoom-in',
          }}
          className="block max-w-[95vw] max-h-[92vh] w-auto h-auto select-none"
        />
      </div>
    </div>
  );
};

export default Lightbox;
